import { Icon } from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  mdiCalculator,
  mdiDeleteOutline,
  mdiInformationOutline,
  mdiPlusBox,
} from "@mdi/js";
import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useId, useMemo, useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { z } from "zod";
import type { Currency } from "../../../../../../../@types/services/Currency";
import type { MeasureUnit } from "../../../../../../../@types/services/MeasureUnit";
import type { Proposal } from "../../../../../../../@types/services/Proposal";
import { showServiceNegotiation } from "../../../../../../../__generated__/orval/endpoints/service-negotiations/service-negotiations";
import type {
  EntitiesServiceNegotiationMemberMainOutput,
  EntitiesServiceNegotiationServiceLineOutput,
} from "../../../../../../../__generated__/orval/models";
import { NumericFormatField } from "../../../../../../../components/atoms/NumericFormatField";
import { DownloadProposals } from "../../../../../../../components/molecules/DownloadProposals";
import { LoadingCentral } from "../../../../../../../components/molecules/LoadingCentral";
import { ForbiddenError } from "../../../../../../../errors/ForbiddenError";
import {
  proposalPdfFileSchema,
  proposalSchema,
} from "../../../../../../../schemas/proposalSchema";
import { fetchCurrencies } from "../../../../../../../services/currency";
import { fetchMeasureUnits } from "../../../../../../../services/measureUnits";
import {
  editProposal,
  fetchProposal,
} from "../../../../../../../services/proposals";
import {
  canEditProposal as canEditProposalByRules,
  hasFractionalSupplierQuantity,
  hasProposalLineChanges,
  isCostBreakdownOverLimit,
  isUnchangedLegacyFractionalSupplierQuantity,
  type ProposalLineValue,
} from "../../../-internal_components/proposalEditRules";

const editHeaderSchema = proposalSchema.omit({ proposal_lines: true }).extend({
  commercialProposal: proposalPdfFileSchema.optional(),
  lead_time: proposalSchema.shape.lead_time.optional(),
  technicalProposal: proposalPdfFileSchema.optional(),
});

type EditHeaderSchema = z.infer<typeof editHeaderSchema>;

interface ProposalSubItem {
  id: string;
  entityId?: string;
  originalQuantity?: string;
  short_text: string;
  quantity: string;
  measure_unit: string;
  gross_price: string;
  isDirect?: boolean;
}

interface SAPCategory {
  id: string;
  serviceLineId: string;
  sapUnit: string;
  subItems: ProposalSubItem[];
}

interface EditProposalFormProps {
  serviceNegotiation: EntitiesServiceNegotiationMemberMainOutput;
  proposal: Proposal;
}

const getProposalLineValues = (
  categories: SAPCategory[],
  additionalItems: ProposalSubItem[],
): ProposalLineValue[] => [
  ...categories.flatMap((category) =>
    category.subItems.map((item) => ({
      grossPrice: Number(item.gross_price),
      measureUnit: item.measure_unit,
      quantity: Number(item.quantity),
      serviceLineId: category.serviceLineId,
      shortText: item.short_text.trim(),
    })),
  ),
  ...additionalItems.map((item) => ({
    grossPrice: Number(item.gross_price),
    measureUnit: item.measure_unit,
    quantity: Number(item.quantity),
    serviceLineId: null,
    shortText: item.short_text.trim(),
  })),
];

const EditProposalForm: React.FC<EditProposalFormProps> = ({
  serviceNegotiation,
  proposal,
}) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isLegacyFixedDeliveryDate =
    proposal.delivery_date != null && proposal.lead_time == null;

  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingCurrencies, setLoadingCurrencies] = useState(true);
  const [loadingMeasureUnits, setLoadingMeasureUnits] = useState(true);
  const [measureUnits, setMeasureUnits] = useState<MeasureUnit[]>([]);

  const [categories, setCategories] = useState<SAPCategory[]>(() => {
    const lines = serviceNegotiation.service_quotation
      .service_lines as unknown as
      | EntitiesServiceNegotiationServiceLineOutput[]
      | undefined;
    return (
      lines?.map((line) => {
        const existingSubItems = proposal.proposal_lines
          .filter(
            (pl) =>
              pl.service_line_id != null &&
              String(pl.service_line_id) === String(line.id),
          )
          .sort((a, b) => a.position - b.position);
        const existingTotal = existingSubItems.reduce(
          (total, item) =>
            total + Number(item.quantity ?? 0) * Number(item.gross_price ?? 0),
          0,
        );
        const existingLine = existingSubItems[0];

        return {
          id: `cat-${line.id}`,
          sapUnit: line.measure_unit.name,
          serviceLineId: String(line.id),
          subItems: [
            {
              entityId: existingLine?.id,
              gross_price:
                line.quantity > 0 ? String(existingTotal / line.quantity) : "",
              id: `sap-${line.id}`,
              isDirect: true,
              measure_unit: line.measure_unit.id.toString(),
              quantity: String(line.quantity),
              short_text: line.short_text,
            },
          ],
        };
      }) ?? []
    );
  });

  const [additionalItems, setAdditionalItems] = useState<ProposalSubItem[]>(
    () =>
      proposal.proposal_lines
        .filter((pl) => pl.service_line_id == null)
        .sort((a, b) => a.position - b.position)
        .map((pl) => ({
          entityId: pl.id,
          gross_price: pl.gross_price?.toString() ?? "",
          id: `existing-${pl.id}`,
          measure_unit: pl.measure_unit?.id.toString() ?? "",
          originalQuantity: pl.quantity?.toString() ?? "",
          quantity: pl.quantity?.toString() ?? "",
          short_text: pl.short_text ?? "",
        })),
  );

  const [initialProposalLineValues] = useState(() =>
    getProposalLineValues(categories, additionalItems),
  );
  const proposalLinesChanged = hasProposalLineChanges(
    initialProposalLineValues,
    getProposalLineValues(categories, additionalItems),
  );

  const {
    clearErrors,
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: {
      commercialProposal: undefined,
      currency: proposal?.currency?.id?.toString() ?? "",
      lead_time: proposal?.lead_time ?? undefined,
      technicalProposal: undefined,
    },
    resolver: zodResolver(editHeaderSchema),
  });

  const currencySelectId = useId();

  const updateSubItem = useCallback(
    (
      categoryId: string,
      subItemId: string,
      updates: Partial<ProposalSubItem>,
    ) => {
      setCategories((prev) =>
        prev.map((cat) => {
          if (cat.id !== categoryId) return cat;
          return {
            ...cat,
            subItems: cat.subItems.map((item) =>
              item.id === subItemId ? { ...item, ...updates } : item,
            ),
          };
        }),
      );
    },
    [],
  );

  const addAdditionalItem = useCallback(() => {
    setAdditionalItems((prev) => [
      ...prev,
      {
        gross_price: "",
        id: Math.random().toString(36).slice(2, 11),
        measure_unit: "",
        quantity: "1",
        short_text: "",
      },
    ]);
  }, []);

  const removeAdditionalItem = useCallback((id: string) => {
    setAdditionalItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const updateAdditionalItem = useCallback(
    (id: string, updates: Partial<ProposalSubItem>) => {
      setAdditionalItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, ...updates } : item)),
      );
    },
    [],
  );

  const grandTotal = useMemo(() => {
    const categoriesTotal = categories.reduce(
      (acc, cat) =>
        acc +
        cat.subItems.reduce(
          (sAcc, item) =>
            sAcc + Number(item.quantity) * Number(item.gross_price),
          0,
        ),
      0,
    );
    return categoriesTotal;
  }, [categories]);

  const additionalTotal = useMemo(
    () =>
      additionalItems.reduce(
        (acc, item) => acc + Number(item.quantity) * Number(item.gross_price),
        0,
      ),
    [additionalItems],
  );

  const costBreakdownExceedsProposalTotal = isCostBreakdownOverLimit(
    additionalTotal,
    grandTotal,
    proposalLinesChanged,
  );

  const formatValue = useCallback(
    (val: number) =>
      val.toLocaleString(i18n.language, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      }),
    [i18n.language],
  );

  const handleProposalFileChange = useCallback(
    (
      fieldName: "technicalProposal" | "commercialProposal",
      file: File | undefined,
      onChange: (value: File | undefined) => void,
      input: HTMLInputElement,
    ) => {
      if (!file) {
        clearErrors(fieldName);
        onChange(undefined);
        return;
      }

      const parsedFile = proposalPdfFileSchema.safeParse(file);

      if (!parsedFile.success) {
        onChange(undefined);
        input.value = "";
        setError(fieldName, {
          message: parsedFile.error.issues[0]?.message,
          type: "manual",
        });
        return;
      }

      clearErrors(fieldName);
      onChange(file);
    },
    [clearErrors, setError],
  );

  useEffect(() => {
    fetchMeasureUnits({
      filters: { name: ["DAY", "MON", "LE", "EA", "H", "PC"] },
    })
      .then((data) => setMeasureUnits(data.results))
      .finally(() => setLoadingMeasureUnits(false));

    fetchCurrencies({ filters: { enabled: true } })
      .then((resource) => setCurrencies(resource.results))
      .finally(() => setLoadingCurrencies(false));
  }, []);

  const onSubmit: SubmitHandler<EditHeaderSchema> = useCallback(
    (headerData) => {
      if (costBreakdownExceedsProposalTotal) {
        enqueueSnackbar(t("newProposal.messages.costBreakdownLimitExceeded"), {
          variant: "warning",
        });
        return;
      }

      const allSubItems = [
        ...categories.flatMap((cat) =>
          cat.subItems.map((item) => ({
            ...item,
            serviceLineId: cat.serviceLineId,
          })),
        ),
        ...additionalItems.map((item) => ({ ...item, serviceLineId: null })),
      ];

      if (proposalLinesChanged && allSubItems.length === 0) {
        enqueueSnackbar(t("newProposal.messages.atLeastOneLine"), {
          variant: "warning",
        });
        return;
      }

      if (proposalLinesChanged && hasFractionalSupplierQuantity(allSubItems)) {
        enqueueSnackbar(t("newProposal.messages.quantityMustBeWholeNumber"), {
          variant: "warning",
        });
        return;
      }

      const proposal_lines = proposalLinesChanged
        ? allSubItems.map((item, idx) => ({
            gross_price: Number(item.gross_price),
            id: item.entityId,
            measure_unit: item.measure_unit,
            position: (idx + 1) * 10,
            quantity: Number(item.quantity),
            service_line_id: item.serviceLineId ?? null,
            short_text: item.short_text.trim(),
          }))
        : undefined;

      setLoading(true);
      editProposal(
        {
          ...headerData,
          ...(proposal_lines !== undefined ? { proposal_lines } : {}),
        },
        proposal.id,
      )
        .then(() => {
          navigate({
            params: { serviceNegotiationId: String(serviceNegotiation.id) },
            to: "/vbuyer/service_negotiations/$serviceNegotiationId",
          });
          enqueueSnackbar(t("newProposal.editSuccess"), {
            variant: "success",
          });
        })
        .catch(() => {
          enqueueSnackbar(t("newProposal.messages.editError"), {
            variant: "error",
          });
        })
        .finally(() => setLoading(false));
    },
    [
      categories,
      additionalItems,
      proposalLinesChanged,
      costBreakdownExceedsProposalTotal,
      proposal.id,
      serviceNegotiation.id,
      navigate,
      t,
    ],
  );

  if (loadingCurrencies || loadingMeasureUnits) {
    return <LoadingCentral />;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box
        sx={{
          alignItems: "flex-start",
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: "bold" }} variant="h5">
            {t("newProposal.titleEdit")}
          </Typography>
          <Alert severity="info" sx={{ mb: 2, width: "100%" }}>
            {t("serviceNegotiation.technicalProposalNoValuesAlert")}
          </Alert>
          <Typography sx={{ color: "text.secondary", mt: 0.5 }} variant="body2">
            {t("newProposal.serviceQuotation")} #{" "}
            {serviceNegotiation.service_quotation?.identifier}
          </Typography>
          <Typography sx={{ color: "text.secondary" }} variant="body2">
            {serviceNegotiation.service_quotation?.short_description}
          </Typography>
          {!!proposal && (
            <Box sx={{ mt: 1 }}>
              <DownloadProposals proposal={proposal} />
            </Box>
          )}
        </Box>

        <Card
          sx={{ alignItems: "center", display: "flex", gap: 1.5, p: 2 }}
          variant="outlined"
        >
          <Icon icon={mdiCalculator} />
          <Box>
            <Typography
              sx={{
                color: "text.secondary",
                fontWeight: "bold",
                letterSpacing: 1,
                textTransform: "uppercase",
              }}
              variant="caption"
            >
              {t("common.total")}
            </Typography>
            <Typography
              color="primary"
              sx={{ fontWeight: "bold" }}
              variant="h5"
            >
              {formatValue(grandTotal)}
            </Typography>
          </Box>
        </Card>
      </Box>

      <Alert
        icon={<Icon icon={mdiInformationOutline} />}
        severity="info"
        sx={{ mb: 3 }}
      >
        <Typography sx={{ fontWeight: "bold" }} variant="body2">
          {t("newProposal.instructions.title")}
        </Typography>
        <Typography variant="body2">
          {t("newProposal.instructions.officialLines")}
        </Typography>
      </Alert>

      <Alert
        icon={<Icon icon={mdiInformationOutline} />}
        severity="warning"
        sx={{ mb: 3 }}
      >
        <Typography variant="body2">
          {t("newProposal.grossPriceWarning")}
        </Typography>
      </Alert>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
        {!isLegacyFixedDeliveryDate && (
          <Controller
            control={control}
            name="lead_time"
            render={({ field }) => (
              <TextField
                error={!!errors.lead_time}
                helperText={errors.lead_time?.message}
                label={t("newProposal.leadTimeDays")}
                name={field.name}
                onBlur={field.onBlur}
                onChange={field.onChange}
                required
                size="small"
                slotProps={{ htmlInput: { min: 1, step: 1 } }}
                type="number"
                value={field.value ?? ""}
              />
            )}
          />
        )}

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id={currencySelectId}>
            {t("newProposal.currency")}
          </InputLabel>
          <Controller
            control={control}
            name="currency"
            render={({ field }) => (
              <Select
                error={!!errors.currency}
                label={t("newProposal.currency")}
                labelId={currencySelectId}
                name={field.name}
                onBlur={field.onBlur}
                onChange={field.onChange}
                required
                value={field.value}
              >
                {currencies.map((cur) => (
                  <MenuItem key={cur.id} value={cur.id.toString()}>
                    {cur.name} - {cur.unit}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          {!!errors.currency && (
            <FormHelperText error>{errors.currency.message}</FormHelperText>
          )}
        </FormControl>

        <Box sx={{ display: "flex", flex: 1, gap: 2, minWidth: 300 }}>
          <Controller
            control={control}
            name="technicalProposal"
            render={({ field }) => (
              <TextField
                error={!!errors.technicalProposal}
                helperText={errors.technicalProposal?.message}
                label={t("newProposal.technicalProposal")}
                name={field.name}
                onBlur={field.onBlur}
                onChange={(e) => {
                  const input = e.target as HTMLInputElement;
                  handleProposalFileChange(
                    "technicalProposal",
                    input.files?.[0],
                    field.onChange,
                    input,
                  );
                }}
                size="small"
                slotProps={{
                  htmlInput: {
                    accept: "application/pdf,.pdf",
                    multiple: false,
                  },
                  inputLabel: { shrink: true },
                }}
                sx={{ flex: 1 }}
                type="file"
              />
            )}
          />

          <Controller
            control={control}
            name="commercialProposal"
            render={({ field }) => (
              <TextField
                error={!!errors.commercialProposal}
                helperText={errors.commercialProposal?.message}
                label={t("newProposal.commercialProposal")}
                name={field.name}
                onBlur={field.onBlur}
                onChange={(e) => {
                  const input = e.target as HTMLInputElement;
                  handleProposalFileChange(
                    "commercialProposal",
                    input.files?.[0],
                    field.onChange,
                    input,
                  );
                }}
                size="small"
                slotProps={{
                  htmlInput: {
                    accept: "application/pdf,.pdf",
                    multiple: false,
                  },
                  inputLabel: { shrink: true },
                }}
                sx={{ flex: 1 }}
                type="file"
              />
            )}
          />
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ mb: 3 }} variant="outlined">
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "grey.50" }}>
              <TableCell
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: "bold",
                  letterSpacing: 1,
                  textTransform: "uppercase",
                  width: "35%",
                }}
              >
                {t("newProposal.shortText")}
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: "bold",
                  letterSpacing: 1,
                  textTransform: "uppercase",
                }}
              >
                {t("newProposal.quantity")}
              </TableCell>
              <TableCell
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: "bold",
                  letterSpacing: 1,
                  textTransform: "uppercase",
                }}
              >
                {t("newProposal.measureUnit")}
              </TableCell>
              <TableCell
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: "bold",
                  letterSpacing: 1,
                  textTransform: "uppercase",
                }}
              >
                {t("newProposal.grossPrice")}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: "bold",
                  letterSpacing: 1,
                  textTransform: "uppercase",
                }}
              >
                {t("common.total")}
              </TableCell>
              <TableCell sx={{ width: 48 }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {categories.map((cat) => (
              <React.Fragment key={cat.id}>
                {cat.subItems.map((item) => (
                  <TableRow
                    key={item.id}
                    sx={{ "&:hover": { bgcolor: "action.hover" } }}
                  >
                    <TableCell sx={{ py: 1 }}>
                      <TextField
                        disabled
                        fullWidth
                        size="small"
                        value={item.short_text}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>
                      <TextField
                        disabled
                        size="small"
                        sx={{ width: 100 }}
                        value={item.quantity}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography>{cat.sapUnit}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <TextField
                        onChange={(e) =>
                          updateSubItem(cat.id, item.id, {
                            gross_price: e.target.value,
                          })
                        }
                        required
                        size="small"
                        slotProps={{
                          input: {
                            inputComponent:
                              NumericFormatField as React.ComponentType<unknown>,
                          },
                        }}
                        sx={{ width: 120 }}
                        value={item.gross_price}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1 }}>
                      <Typography sx={{ fontWeight: "medium" }} variant="body2">
                        {formatValue(
                          Number(item.quantity) * Number(item.gross_price),
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography />
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}

            <TableRow>
              <TableCell colSpan={6} sx={{ borderBottom: 0, p: 0 }}>
                <Alert severity="info" sx={{ mb: 1 }}>
                  {t("newProposal.costBreakdownNote")}
                </Alert>
              </TableCell>
            </TableRow>

            <TableRow sx={{ bgcolor: "grey.200" }}>
              <TableCell colSpan={6} sx={{ py: 1 }}>
                <Typography
                  sx={{
                    color: "text.secondary",
                    fontWeight: "bold",
                    letterSpacing: 2,
                    textTransform: "uppercase",
                  }}
                  variant="caption"
                >
                  {t("newProposal.costBreakdown")}
                </Typography>
              </TableCell>
            </TableRow>

            {additionalItems.map((item) => {
              const allowsLegacyFraction =
                isUnchangedLegacyFractionalSupplierQuantity({
                  ...item,
                  serviceLineId: null,
                });

              return (
                <TableRow
                  key={item.id}
                  sx={{ "&:hover": { bgcolor: "action.hover" } }}
                >
                  <TableCell sx={{ py: 1 }}>
                    <TextField
                      fullWidth
                      onChange={(e) =>
                        updateAdditionalItem(item.id, {
                          short_text: e.target.value,
                        })
                      }
                      placeholder={t("newProposal.placeholder.additionalItem")}
                      required
                      size="small"
                      slotProps={{ htmlInput: { maxLength: 40 } }}
                      value={item.short_text}
                      variant="standard"
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1 }}>
                    <TextField
                      onChange={(e) =>
                        updateAdditionalItem(item.id, {
                          quantity: e.target.value,
                        })
                      }
                      required
                      size="small"
                      slotProps={{
                        htmlInput: {
                          min: allowsLegacyFraction ? undefined : 1,
                          step: allowsLegacyFraction ? "any" : 1,
                          style: { textAlign: "center" },
                        },
                      }}
                      sx={{ width: 100 }}
                      type="number"
                      value={item.quantity}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        onChange={(e) =>
                          updateAdditionalItem(item.id, {
                            measure_unit: e.target.value,
                          })
                        }
                        required
                        value={item.measure_unit}
                      >
                        {measureUnits.map((unit) => (
                          <MenuItem key={unit.id} value={unit.id.toString()}>
                            {unit.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <TextField
                      onChange={(e) =>
                        updateAdditionalItem(item.id, {
                          gross_price: e.target.value,
                        })
                      }
                      required
                      size="small"
                      slotProps={{
                        input: {
                          inputComponent:
                            NumericFormatField as React.ComponentType<unknown>,
                        },
                      }}
                      sx={{ width: 120 }}
                      value={item.gross_price}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: "medium" }} variant="body2">
                      {formatValue(
                        Number(item.quantity) * Number(item.gross_price),
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Tooltip title={t("common.remove")}>
                      <IconButton
                        color="error"
                        onClick={() => removeAdditionalItem(item.id)}
                        size="small"
                      >
                        <Icon icon={mdiDeleteOutline} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}

            {additionalItems.length > 0 && (
              <TableRow sx={{ borderColor: "divider", borderTop: 1 }}>
                <TableCell align="right" colSpan={4}>
                  <Typography color="text.secondary" variant="body2">
                    {t("newProposal.costBreakdownTotal")}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography sx={{ fontWeight: "bold" }} variant="body2">
                    {formatValue(additionalTotal)}
                  </Typography>
                </TableCell>
                <TableCell />
              </TableRow>
            )}

            {costBreakdownExceedsProposalTotal && (
              <TableRow>
                <TableCell colSpan={6} sx={{ borderBottom: 0, p: 0 }}>
                  <Alert severity="error" sx={{ mb: 1 }}>
                    {t("newProposal.messages.costBreakdownLimitExceeded")}
                  </Alert>
                </TableCell>
              </TableRow>
            )}

            <TableRow>
              <TableCell colSpan={6} sx={{ py: 2 }}>
                <Button
                  color="warning"
                  disabled={costBreakdownExceedsProposalTotal}
                  fullWidth
                  onClick={addAdditionalItem}
                  size="small"
                  startIcon={<Icon icon={mdiPlusBox} />}
                  sx={{ border: "2px dashed", borderRadius: 2 }}
                  variant="outlined"
                >
                  {t("newProposal.addAdditionalLine")}
                </Button>
              </TableCell>
            </TableRow>

            <TableRow sx={{ bgcolor: "grey.50", borderTop: "2px solid" }}>
              <TableCell>
                <Typography sx={{ fontWeight: "bold" }} variant="h6">
                  {t("newProposal.grandTotal")}
                </Typography>
              </TableCell>
              <TableCell colSpan={3} />
              <TableCell align="right">
                <Typography
                  color="primary"
                  sx={{ fontWeight: "bold" }}
                  variant="h5"
                >
                  {formatValue(grandTotal)}
                </Typography>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          borderColor: "divider",
          borderTop: 1,
          display: "flex",
          justifyContent: "flex-end",
          pt: 2,
        }}
      >
        <Button
          color="success"
          disabled={costBreakdownExceedsProposalTotal}
          loading={loading}
          loadingPosition="center"
          type="submit"
          variant="contained"
        >
          {t("newProposal.button.send")}
        </Button>
      </Box>
    </form>
  );
};

const EditProposal: React.FC = () => {
  const serviceNegotiation = Route.useLoaderData();

  const { proposalId } = Route.useParams();
  const serviceNegotiationProposalId =
    serviceNegotiation?.proposal?.id?.toString() ?? "";

  const [proposal, setProposal] = useState<Proposal>();
  const [loading, setLoading] = useState(true);

  const canEditProposal = canEditProposalByRules(
    serviceNegotiation?.status?.name,
    serviceNegotiation?.service_quotation?.offer_submission_deadline,
  );

  const handleFetchProposal = useCallback((id: string) => {
    setLoading(() => true);
    fetchProposal(id)
      .then((resource) => {
        setProposal(resource);
      })
      .finally(() => {
        setLoading(() => false);
      });
  }, []);

  useEffect(() => {
    if (
      canEditProposal &&
      proposalId &&
      proposalId === serviceNegotiationProposalId
    ) {
      handleFetchProposal(serviceNegotiationProposalId);
    }
  }, [
    canEditProposal,
    handleFetchProposal,
    proposalId,
    serviceNegotiationProposalId,
  ]);

  if (!proposalId || proposalId !== serviceNegotiationProposalId) {
    return <Navigate to="/not_found" />;
  }

  if (!canEditProposal) {
    return <Navigate to=".." />;
  }

  if (loading) {
    return <LoadingCentral />;
  }

  if (!proposal || proposal.id !== proposalId) {
    return <Navigate to="/not_found" />;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <EditProposalForm
        proposal={proposal}
        serviceNegotiation={serviceNegotiation}
      />
    </Container>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/service_negotiations/$serviceNegotiationId/proposals/$proposalId/edit",
)({
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Service Negotiation - View")) {
      return;
    }

    throw new ForbiddenError();
  },
  component: EditProposal,
  loader: ({ params: { serviceNegotiationId } }) =>
    showServiceNegotiation(parseInt(serviceNegotiationId, 10)),
});
